
import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRecipes } from '@/contexts/RecipesContext';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import RecipeCard from '@/components/RecipeCard';
import { Spinner } from '@/components/Spinner';
import { Heart } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import ProtectedRoute from '@/components/ProtectedRoute';
import { supabase } from '@/integrations/supabase/client';
import Layout from '@/components/Layout';

const Favorites = () => {
  const { user } = useAuth();
  const { recipes, loading } = useRecipes();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('title-asc');
  const [recipeLikes, setRecipeLikes] = useState<Record<string, number>>({});
  
  const favoriteRecipes = recipes.filter(recipe => 
    user?.favorites?.includes(recipe.id)
  );
  
  // Single batch call to get like counts for all recipes
  useEffect(() => {
    const fetchRecipeLikesCount = async () => {
      try {
        console.log('Fetching recipe likes count in batch for favorites...');
        const { data, error } = await supabase.rpc('get_recipe_like_counts');
        
        if (error) {
          console.error('Error fetching recipe likes:', error);
          return;
        }
        
        if (data) {
          const likesMap: Record<string, number> = {};
          data.forEach((item: {recipe_id: string, count: number}) => {
            likesMap[item.recipe_id] = item.count;
          });
          setRecipeLikes(likesMap);
          console.log(`✅ Loaded ${data.length} recipe like counts for favorites in single batch`);
        }
      } catch (error) {
        console.error('Error in fetchRecipeLikesCount:', error);
      }
    };
    
    fetchRecipeLikesCount();
  }, []);
  
  const filteredRecipes = favoriteRecipes.filter(recipe => 
    recipe.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
    recipe.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
  );
  
  const sortedRecipes = [...filteredRecipes].sort((a, b) => {
    switch (sortBy) {
      case 'title-asc':
        return a.title.localeCompare(b.title);
      case 'title-desc':
        return b.title.localeCompare(a.title);
      case 'calories-asc':
        return a.calories - b.calories;
      case 'calories-desc':
        return b.calories - a.calories;
      case 'protein-desc':
        return b.protein - a.protein;
      case 'likes-desc':
        return (recipeLikes[b.id] || 0) - (recipeLikes[a.id] || 0);
      default:
        return 0;
    }
  });

  return (
    <ProtectedRoute>
      <Layout>
        <div className="flex flex-col min-h-screen">
          <main className="flex-grow bg-gray-50">
            <div className="w-full">
              <div className="flex items-center gap-3 px-4 py-6">
                <Heart className="text-red-500 h-8 w-8" />
                <h1 className="text-3xl font-bold text-vegana-primary">Recetas Favoritas</h1>
              </div>
              
              <div className="w-full px-4 mb-8 space-y-4">
                <div>
                  <Label htmlFor="search">Buscar</Label>
                  <Input
                    id="search"
                    type="text"
                    placeholder="Buscar por título, descripción o etiquetas..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label htmlFor="sort">Ordenar por</Label>
                  <Select
                    value={sortBy}
                    onValueChange={setSortBy}
                  >
                    <SelectTrigger id="sort" className="mt-1">
                      <SelectValue placeholder="Elegir orden" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="title-asc">Título (A-Z)</SelectItem>
                      <SelectItem value="title-desc">Título (Z-A)</SelectItem>
                      <SelectItem value="calories-asc">Calorías (menor a mayor)</SelectItem>
                      <SelectItem value="calories-desc">Calorías (mayor a menor)</SelectItem>
                      <SelectItem value="protein-desc">Proteínas (mayor a menor)</SelectItem>
                      <SelectItem value="likes-desc">Más likes</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {loading ? (
                <div className="flex justify-center my-20">
                  <Spinner size="lg" />
                </div>
              ) : sortedRecipes.length > 0 ? (
                <div className="grid gap-6 px-4">
                  {sortedRecipes.map((recipe) => (
                    <RecipeCard key={recipe.id} recipe={recipe} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <Heart className="h-16 w-16 mx-auto text-gray-300 mb-4" />
                  <h2 className="text-xl font-medium text-gray-600 mb-2">
                    {searchQuery ? "No se encontraron recetas" : "No tienes recetas favoritas"}
                  </h2>
                  <p className="text-gray-500">
                    {searchQuery 
                      ? "Prueba con otra búsqueda"
                      : "Explora nuestras recetas y guarda tus favoritas aquí"
                    }
                  </p>
                </div>
              )}
            </div>
          </main>
        </div>
      </Layout>
    </ProtectedRoute>
  );
};

export default Favorites;
